import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CompanyFilters from './CompanyFilters';
import CompanyRow from './CompanyRow';

const HEADERS = [
  { label: 'Empresa', align: 'left' },
  { label: 'Criada em', align: 'left' },
  { label: 'Usuários', align: 'center' },
  { label: 'Pacientes', align: 'center' },
  { label: 'Agendamentos', align: 'center' },
  { label: 'Último login', align: 'left' },
  { label: 'Última atividade', align: 'left' },
  { label: 'Status', align: 'left' },
  { label: 'Teste', align: 'left' },
  { label: 'Ações', align: 'right' },
];

/**
 * CompaniesTable - Tabela de empresas com filtros e paginação server-side (Admin Geral)
 * @param {Object} props
 * @param {Array} props.companies
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {Object} props.pagination - { total, page, limit, pages }
 * @param {Object} props.filters - props repassadas ao CompanyFilters
 * @param {Function} props.onPageChange
 * @param {Function} props.onLimitChange
 * @param {Function} props.onView
 * @param {Function} props.onToggleTest
 * @param {Function} props.onDelete
 */
function CompaniesTable({
  companies = [],
  isLoading = false,
  error = null,
  pagination = { total: 0, page: 1, limit: 25, pages: 1 },
  filters,
  onPageChange,
  onLimitChange,
  onView,
  onToggleTest,
  onDelete,
}) {
  const { total = 0, page = 1, limit = 25, pages = 1 } = pagination;
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
      }}
    >
      <CompanyFilters {...filters} />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {HEADERS.map((h) => (
                <TableCell
                  key={h.label}
                  align={h.align}
                  sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma empresa encontrada com os filtros aplicados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  onView={onView}
                  onToggleTest={onToggleTest}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderTop: '1px solid #F0F0F0',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Mostrando {startItem} a {endItem} de {total} empresas
        </Typography>

        <Pagination
          count={pages}
          page={page}
          onChange={(_, value) => onPageChange(value)}
          shape="rounded"
          color="primary"
          size="small"
          siblingCount={2}
          boundaryCount={1}
        />

        <Select
          size="small"
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          renderValue={(val) => `${val} por página`}
          sx={{
            fontSize: '0.8125rem',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
          }}
        >
          <MenuItem value={10}>10 por página</MenuItem>
          <MenuItem value={25}>25 por página</MenuItem>
          <MenuItem value={50}>50 por página</MenuItem>
        </Select>
      </Box>
    </Paper>
  );
}

export default CompaniesTable;
